/**
 * The BLOCK-SET contract for a planned interview round (operator ruling 2026-07-25).
 *
 * A turn asks a BLOCK OF QUESTIONS, not one question. The round is already the visible
 * batch — the interview surface renders every block of a round at once and the server
 * generates them in ONE parallel fan-out — so "a block of 1..n cards per turn" is a
 * property of the PLAN, not a new event shape: a round must carry SEVERAL blocks, and
 * those blocks must use DIFFERENT elicitation primitives. A vignette beside a why_ladder
 * beside a semantic_differential triangulates one value from three angles; three vignettes
 * ask the same question three times.
 *
 * It lives here, beside `card-structure.ts`, for the same reason that one does: it is a
 * SEMANTIC rule ArkType cannot state (the shape `RoundPlanRound[]` is valid either way),
 * and it must be ONE predicate shared by the planner's grade-and-re-ask loop, the
 * deterministic planner floor's own construction, and the planner prompt's instruction
 * text — so the stated contract can never drift from the enforced one.
 *
 * NON-THROWING, and deliberately NOT a `.narrow()` on `RoundPlanDraft`: a thin or uniform
 * round is a WEAKER plan, not a broken one. A hard gate would drop the entire adaptive
 * plan to the deterministic floor over a preference — strictly worse for the candidate.
 * The seam re-asks once with the reason, then keeps what it has.
 */

import type { RoundPlanRound } from "./inferred"

/** How many blocks one round may carry. Two is the floor at which a round is a BLOCK at
 *  all; four is the ceiling at which the batch is still answerable in one sitting. */
export const ROUND_BLOCK_SET = { min: 2, max: 4 } as const

/** The block fields the contract reads — a structural subset of `RoundPlanRound`. */
export type StructuralRound = Pick<RoundPlanRound, "round" | "blocks">

/**
 * The reason a round violates the block-set contract, or `null` when it satisfies it:
 * too few blocks, too many, or every block on the same primitive.
 */
export function roundBlockSetGap(round: StructuralRound): string | null {
  const { min, max } = ROUND_BLOCK_SET
  const n = round.blocks.length
  if (n < min) return `round ${round.round} has ${n} block(s) — a turn must ask a BLOCK of at least ${min}`
  if (n > max) return `round ${round.round} has ${n} blocks — more than ${max} is no longer one sitting`
  const primitives = new Set(round.blocks.map((b) => b.primitive))
  if (primitives.size === 1) {
    return `round ${round.round} uses only \`${round.blocks[0]!.primitive}\` for all ${n} blocks — the components of a block must DIFFER`
  }
  return null
}

/** The first block-set violation across a plan's rounds, or `null` when every round holds. */
export function planBlockSetGap(plan: { rounds: readonly StructuralRound[] }): string | null {
  for (const r of plan.rounds) {
    const gap = roundBlockSetGap(r)
    if (gap) return gap
  }
  return null
}

/** The contract as a prompt-ready English instruction block — ONE source of truth shared by
 *  the planner persona and the seam's re-ask, so the stated rule is the graded one. */
export const ROUND_BLOCK_SET_INSTRUCTIONS = [
  `The candidate sees an ENTIRE round at once and answers it as one sitting, so a round is NEVER a single question: plan ${ROUND_BLOCK_SET.min}–${ROUND_BLOCK_SET.max} blocks per round (${ROUND_BLOCK_SET.min}–3 is the sweet spot).`,
  "The blocks WITHIN one round MUST use DIFFERENT `primitive`s. A round of three vignettes is a defect; a vignette + a why_ladder + a semantic_differential is the target — different primitives ask for different mental operations, so the block triangulates the same value from several sides instead of asking it several times.",
  "Make the blocks of a round COHERENT but not redundant: one round = one value (or one tension between two), approached through a lived EPISODE, a felt BOUNDARY, and a concrete SITUATION. Never two blocks the candidate could answer with the same sentence.",
].join("\n")
